import { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { MaskedLines, Reveal } from "../components/Motion";
import { SITE, waLink, PROJECTS } from "../lib/data";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", project: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email and phone.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, source: "contact-page" });
      toast.success("Thank you — we'll be in touch shortly.", {
        description: "Your enquiry has reached the team.",
      });
      setForm({ name: "", email: "", phone: "", project: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try WhatsApp instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-32 md:pt-44">
      <section className="max-w-[1400px] mx-auto px-6 md:px-10">
        <p className="overline text-[#C19A6B] mb-6">Contact</p>
        <h1 className="font-serif-lux text-[#1A1A1A] text-[13vw] md:text-[8vw] leading-[0.9] tracking-tight">
          <MaskedLines lines={["Let's find your", "next address."]} />
        </h1>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 grid md:grid-cols-12 gap-16">
        {/* Form */}
        <Reveal className="md:col-span-7">
          <form data-testid="lead-form" onSubmit={submit} className="space-y-7">
            <div className="grid md:grid-cols-2 gap-7">
              <div>
                <Label className="overline text-[#5C5C5C]">Name *</Label>
                <Input data-testid="lead-name" value={form.name} onChange={set("name")} placeholder="Your full name"
                  className="mt-3 rounded-none border-0 border-b border-[#E5DFD3] bg-transparent px-0 focus-visible:ring-0 focus-visible:border-[#1A1A1A] font-sans-lux text-lg" />
              </div>
              <div>
                <Label className="overline text-[#5C5C5C]">Phone *</Label>
                <Input data-testid="lead-phone" value={form.phone} onChange={set("phone")} placeholder="+91 ..."
                  className="mt-3 rounded-none border-0 border-b border-[#E5DFD3] bg-transparent px-0 focus-visible:ring-0 focus-visible:border-[#1A1A1A] font-sans-lux text-lg" />
              </div>
            </div>
            <div>
              <Label className="overline text-[#5C5C5C]">Email *</Label>
              <Input data-testid="lead-email" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com"
                className="mt-3 rounded-none border-0 border-b border-[#E5DFD3] bg-transparent px-0 focus-visible:ring-0 focus-visible:border-[#1A1A1A] font-sans-lux text-lg" />
            </div>
            <div>
              <Label className="overline text-[#5C5C5C]">Project of interest</Label>
              <Select value={form.project} onValueChange={(v) => setForm((f) => ({ ...f, project: v }))}>
                <SelectTrigger data-testid="lead-project" className="mt-3 rounded-none border-0 border-b border-[#E5DFD3] bg-transparent px-0 focus:ring-0 font-sans-lux text-lg h-11">
                  <SelectValue placeholder="Select a project" />
                </SelectTrigger>
                <SelectContent className="bg-[#FDFBF7]">
                  {PROJECTS.map((p) => (
                    <SelectItem key={p.slug} value={p.name}>{p.name} — {p.city}</SelectItem>
                  ))}
                  <SelectItem value="Not sure yet">Not sure yet</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline text-[#5C5C5C]">Message</Label>
              <Textarea data-testid="lead-message" value={form.message} onChange={set("message")} rows={4} placeholder="Tell us what you're looking for..."
                className="mt-3 rounded-none border-0 border-b border-[#E5DFD3] bg-transparent px-0 focus-visible:ring-0 focus-visible:border-[#1A1A1A] font-sans-lux text-lg resize-none" />
            </div>
            <button
              type="submit"
              data-testid="lead-submit"
              disabled={loading}
              className="inline-flex items-center gap-3 bg-[#1A1A1A] text-[#FDFBF7] btn-lux btn-lux-invert px-10 py-5 overline disabled:opacity-60"
            >
              {loading ? "Sending..." : "Book a Site Visit"} <ArrowUpRight size={16} />
            </button>
          </form>
        </Reveal>

        {/* Contact info */}
        <Reveal delay={0.15} className="md:col-span-5">
          <div className="border border-[#E5DFD3] bg-[#F5F2EB] p-8 md:p-10">
            <p className="overline text-[#C19A6B] mb-6">Direct lines</p>
            <ul className="space-y-6 font-sans-lux">
              <li className="flex items-center gap-4">
                <Phone size={18} className="text-[#6E7765]" />
                <a href={`tel:${SITE.phoneRaw}`} className="text-[#1A1A1A] link-sweep">{SITE.phone}</a>
              </li>
              <li className="flex items-center gap-4">
                <Mail size={18} className="text-[#6E7765]" />
                <a href={`mailto:${SITE.email}`} className="text-[#1A1A1A] link-sweep">{SITE.email}</a>
              </li>
              <li className="flex items-center gap-4">
                <MapPin size={18} className="text-[#6E7765]" />
                <span className="text-[#1A1A1A]">{SITE.location}</span>
              </li>
            </ul>

            <div className="h-px bg-[#E5DFD3] my-8" />
            <p className="font-sans-lux text-[#5C5C5C] text-sm mb-5">
              Prefer to chat? Our team responds fastest on WhatsApp.
            </p>
            <a
              href={waLink()}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-whatsapp-button"
              className="w-full flex items-center justify-center gap-3 bg-[#1FA855] text-white px-6 py-5 overline hover:brightness-110 transition-[filter]"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347M12.05 21.785h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884" />
              </svg>
              Chat on WhatsApp
            </a>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
